import React, { useEffect, useRef, useState } from "react";
import { useGame } from "@/context/GameContext";
import { BUSINESSES } from "@/data/gameData";
import { Sound } from "@/lib/sound";
import FlyerIntro from "./FlyerIntro";
import FlyerResult from "./FlyerResult";

const TYPES = [
  { id: "interested", emoji: "😀", label: "Interested!", weight: 45 },
  { id: "busy", emoji: "🏃", label: "Too busy", weight: 35 },
  { id: "nosign", emoji: "🚫", label: "No flyers sign", weight: 20 },
];
const pickType = () => {
  let r = Math.random() * 100;
  for (const t of TYPES) { if (r < t.weight) return t; r -= t.weight; }
  return TYPES[0];
};
const SPOT_IDS = ["a", "b", "c", "d", "e", "f"];
const GAME_SECS = 20;
const SPOT_STYLE = {
  interested: "bg-emerald-100 border-emerald-400",
  busy: "bg-slate-100 border-slate-300",
  nosign: "bg-rose-100 border-rose-400",
};
const TAP_RESULT = {
  interested: { sound: "coin", stat: "leads", flash: "+1 lead!" },
  busy: { sound: "tap", stat: "wasted", flash: "Flyer wasted" },
  nosign: { sound: "error", stat: "penalties", flash: "Respect the sign! -rep" },
};

const FlyerGame = ({ businessId, onDone, onLearn, onDesign, tipSeen }) => {
  const { state, spendMarketing, addLeads } = useGame();
  const biz = BUSINESSES[businessId];
  const [phase, setPhase] = useState("intro");
  const [pack, setPack] = useState(null);
  const [flyers, setFlyers] = useState(0);
  const [time, setTime] = useState(GAME_SECS);
  const [spots, setSpots] = useState({});
  const [stats, setStats] = useState({ leads: 0, wasted: 0, penalties: 0 });
  const [flash, setFlash] = useState(null);
  const statsRef = useRef(stats);
  statsRef.current = stats;
  const finished = useRef(false);

  const start = (p) => {
    if (!spendMarketing(businessId, p.price)) { Sound.error(); return; }
    setPack(p);
    setFlyers(p.flyers);
    setPhase("play");
  };

  // Timer + neighbor spawner while playing
  useEffect(() => {
    if (phase !== "play") return undefined;
    const timeouts = [];
    const tick = setInterval(() => setTime((t) => Math.max(0, t - 1)), 1000);
    const spawn = setInterval(() => {
      setSpots((s) => {
        const empty = SPOT_IDS.filter((id) => !s[id]);
        if (empty.length === 0) return s;
        const id = empty[Math.floor(Math.random() * empty.length)];
        const key = Date.now();
        timeouts.push(setTimeout(() => setSpots((cur) => (cur[id]?.key === key ? { ...cur, [id]: null } : cur)), 1600));
        return { ...s, [id]: { ...pickType(), key } };
      });
    }, 900);
    return () => { clearInterval(tick); clearInterval(spawn); timeouts.forEach(clearTimeout); };
  }, [phase]);

  // Finish when out of time or flyers
  useEffect(() => {
    if (phase !== "play" || finished.current || (time > 0 && flyers > 0)) return;
    finished.current = true;
    const s = statsRef.current;
    addLeads(businessId, s.leads, s.penalties);
    Sound.success();
    setTimeout(() => setPhase("result"), 400);
  }, [time, flyers, phase, businessId, addLeads]);

  const hand = (id) => {
    const sp = spots[id];
    if (!sp || flyers <= 0 || phase !== "play") return;
    const r = TAP_RESULT[sp.id];
    setSpots((s) => ({ ...s, [id]: null }));
    setFlyers((f) => f - 1);
    Sound[r.sound]();
    setStats((x) => ({ ...x, [r.stat]: x[r.stat] + 1 }));
    setFlash(r.flash);
    setTimeout(() => setFlash(null), 500);
  };

  if (phase === "intro") return <FlyerIntro cash={state.cash} onStart={start} onLearn={onLearn} onDesign={onDesign} onDone={onDone} tipSeen={tipSeen} />;
  if (phase === "result") return <FlyerResult biz={biz} pack={pack} stats={stats} tomorrow={Math.min(3, state.flyerLeads[businessId] || 0)} onDone={onDone} />;

  return (
    <div className="bg-white rounded-3xl border-4 border-orange-300 p-5 animate-pop-in" data-testid="flyer-game-play">
      <div className="flex justify-between items-center mb-3 font-heading font-black flex-wrap gap-2">
        <div className="bg-orange-100 border-2 border-orange-300 rounded-xl px-3 py-1 text-orange-800" data-testid="flyer-count">📄 {flyers} left</div>
        <div className="bg-emerald-100 border-2 border-emerald-300 rounded-xl px-3 py-1 text-emerald-800" data-testid="flyer-leads-live">😀 {stats.leads} leads</div>
        <div className="bg-amber-100 border-2 border-amber-300 rounded-xl px-3 py-1 text-amber-800">⏱ {time}s</div>
      </div>
      <p className="text-xs font-bold text-slate-500 mb-3 text-center h-4">{flash || "Tap 😀 neighbors · avoid 🏃 busy people and 🚫 signs"}</p>
      <div className="grid grid-cols-3 gap-3">
        {SPOT_IDS.map((id, i) => {
          const sp = spots[id];
          return (
            <button
              key={id}
              onClick={() => hand(id)}
              data-testid={`flyer-spot-${i}`}
              className={`h-24 sm:h-28 rounded-2xl border-4 flex flex-col items-center justify-center transition-colors ${sp ? SPOT_STYLE[sp.id] : "bg-orange-50 border-orange-200"}`}
            >
              <span className="text-4xl">{sp ? sp.emoji : "🏠"}</span>
              {sp && <span className="text-[10px] font-black uppercase text-slate-600 mt-1">{sp.label}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FlyerGame;
